import { useEffect, useState, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { automationApi, type OrderAutomationState, type AutomationEvent } from '@/lib/adminApi';
import { toast, Toaster } from 'sonner';
import { Card, CardHeader, CardBody } from '@/components/ui/card';
import { PageHeader, Skeleton } from '@/components/ui/page';
import { Button } from '@/components/ui/button';
import { ArrowLeft, RefreshCw, CheckCircle, XCircle, Clock, Bot, MessageCircle, Send } from 'lucide-react';

const stageLabels: Record<string, string> = {
  new: 'New Order', pending_confirmation: 'Awaiting Confirmation', confirmed: 'Confirmed',
  collecting_data: 'Collecting Customer Data', data_completed: 'Data Collection Complete',
  offering_upsell: 'Offering Upsells', upsell_completed: 'Upsells Complete',
  ready_for_shipping: 'Ready for Shipping', shipped: 'Shipped',
  delivered: 'Delivered', returned: 'Returned', cancelled: 'Cancelled',
};
const stageColors: Record<string, string> = {
  new: 'bg-slate-500/10 text-slate-400 border-slate-500/20',
  pending_confirmation: 'bg-amber-500/10 text-amber-400 border-amber-500/20',
  confirmed: 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20',
  collecting_data: 'bg-blue-500/10 text-blue-400 border-blue-500/20',
  data_completed: 'bg-cyan-500/10 text-cyan-400 border-cyan-500/20',
  offering_upsell: 'bg-purple-500/10 text-purple-400 border-purple-500/20',
  upsell_completed: 'bg-violet-500/10 text-violet-400 border-violet-500/20',
  ready_for_shipping: 'bg-indigo-500/10 text-indigo-400 border-indigo-500/20',
  shipped: 'bg-sky-500/10 text-sky-400 border-sky-500/20',
  delivered: 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20',
  returned: 'bg-red-500/10 text-red-400 border-red-500/20',
  cancelled: 'bg-zinc-500/10 text-zinc-400 border-zinc-500/20',
};

const eventIcons: Record<string, React.ReactNode> = {
  stage_confirmed: <CheckCircle className="h-4 w-4 text-emerald-400" />,
  stage_cancelled: <XCircle className="h-4 w-4 text-red-400" />,
  stage_collecting_data: <Bot className="h-4 w-4 text-blue-400" />,
};

export default function AutomationOrderDetailPage() {
  const { orderId } = useParams<{ orderId: string }>();
  const [state, setState] = useState<OrderAutomationState | null>(null);
  const [events, setEvents] = useState<AutomationEvent[]>([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    if (!orderId) return;
    try {
      const res = await automationApi.orderState(orderId);
      if (res.success) {
        setState(res.data.state);
        setEvents(res.data.events);
      }
    } catch (e: any) {
      toast.error(e.message);
    } finally {
      setLoading(false);
    }
  }, [orderId]);

  useEffect(() => { load(); }, [load]);

  const triggerAi = async () => {
    if (!orderId) return;
    try {
      await automationApi.triggerAiConversation(orderId);
      toast.success('AI conversation triggered');
    } catch (e: any) { toast.error(e.message); }
  };

  if (loading) {
    return (
      <div className="p-4 sm:p-6 space-y-4">
        <Skeleton className="h-8 w-64" />
        <Skeleton className="h-64 rounded-xl" />
      </div>
    );
  }

  if (!state) {
    return (
      <div className="p-4 sm:p-6">
        <PageHeader title="Order Not Found" description="This order does not exist" />
      </div>
    );
  }

  const stage = state.automation_stage || 'new';

  return (
    <div className="p-4 sm:p-6 space-y-4">
      <Toaster position="top-center" richColors />
      <PageHeader
        title={`Order ${state.id?.slice(0, 8)}`}
        description={stageLabels[stage] || stage}
        action={
          <div className="flex gap-2">
            <Link to="/admin/automation">
              <Button size="sm" variant="ghost"><ArrowLeft className="h-3.5 w-3.5 mr-1" /> Back</Button>
            </Link>
            <Button size="sm" variant="outline" onClick={load}><RefreshCw className="h-3.5 w-3.5 mr-1" /> Refresh</Button>
            <Button size="sm" variant="outline" onClick={triggerAi}><Bot className="h-3.5 w-3.5 mr-1" /> Trigger AI</Button>
          </div>
        }
      />

      {/* Current Stage */}
      <div className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full border text-sm ${stageColors[stage] || stageColors.new}`}>
        <span className="font-medium capitalize">{stageLabels[stage] || stage.replace(/_/g, ' ')}</span>
      </div>

      {/* Order + Customer Info */}
      <div className="grid lg:grid-cols-2 gap-4">
        <Card>
          <CardHeader title="Order Details" />
          <CardBody>
            <div className="grid grid-cols-2 gap-3 text-sm">
              <div><span className="text-zinc-500">Status</span><p className="font-medium capitalize">{state.status}</p></div>
              <div><span className="text-zinc-500">Confirmation</span><p className="font-medium capitalize">{state.confirmation_status}</p></div>
              <div><span className="text-zinc-500">Data Collection</span><p className="font-medium capitalize">{(state.data_collection_status || 'pending').replace(/_/g, ' ')}</p></div>
              <div><span className="text-zinc-500">COD Amount</span><p className="font-medium">EGP {state.cod_amount?.toLocaleString()}</p></div>
            </div>
            {state.products?.length > 0 && (
              <div className="mt-3">
                <span className="text-xs text-zinc-500 uppercase">Products</span>
                <div className="mt-1 space-y-1">
                  {state.products.map((p: any, i: number) => (
                    <div key={i} className="text-sm text-zinc-300">{p.name} {p.quantity > 1 ? `x${p.quantity}` : ''} {p.is_upsell && <span className="text-purple-400 text-xs ml-1">(upsell)</span>}</div>
                  ))}
                </div>
              </div>
            )}
          </CardBody>
        </Card>

        <Card>
          <CardHeader title="Customer Data" />
          <CardBody>
            <div className="grid grid-cols-2 gap-3 text-sm">
              <div><span className="text-zinc-500">Name</span><p className="font-medium">{state.customer_name || '—'}</p></div>
              <div><span className="text-zinc-500">Phone</span><p className="font-medium font-mono text-xs">{state.customer_phone || '—'}</p></div>
              <div><span className="text-zinc-500">Governorate</span><p className="font-medium">{state.customer_governorate || '—'}</p></div>
              <div><span className="text-zinc-500">Area/District</span><p className="font-medium">{state.customer_area_district || '—'}</p></div>
              <div className="col-span-2"><span className="text-zinc-500">Full Address</span><p className="font-medium">{state.customer_full_address || '—'}</p></div>
              <div><span className="text-zinc-500">Landmark</span><p className="font-medium">{state.customer_landmark || '—'}</p></div>
              <div><span className="text-zinc-500">Alt Phone</span><p className="font-medium font-mono text-xs">{state.customer_alternative_phone || '—'}</p></div>
            </div>
          </CardBody>
        </Card>
      </div>

      {/* Event Timeline */}
      <Card>
        <CardHeader title="Event Timeline" description={`${events.length} events recorded`} />
        <CardBody>
          {events.length === 0 ? (
            <p className="text-zinc-500 text-sm">No events yet.</p>
          ) : (
            <div className="space-y-0">
              {events.map((evt, idx) => (
                <div key={evt.id} className="flex gap-3 py-2 border-b border-white/[0.04] last:border-0">
                  <div className="mt-0.5 flex-shrink-0">
                    {eventIcons[evt.event_type] || <Clock className="h-4 w-4 text-zinc-600" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium capitalize">{evt.event_type.replace(/_/g, ' ')}</span>
                      <span className="text-xs text-zinc-500">{evt.source}</span>
                    </div>
                    {evt.previous_status && (
                      <div className="text-xs text-zinc-500 mt-0.5">
                        {evt.previous_status} → {evt.new_status}
                      </div>
                    )}
                    {evt.details && Object.keys(evt.details).length > 0 && (
                      <div className="text-xs text-zinc-600 mt-0.5 font-mono">
                        {JSON.stringify(evt.details).slice(0, 120)}
                      </div>
                    )}
                    <div className="text-xs text-zinc-600 mt-0.5">
                      {new Date(evt.created_at).toLocaleString()}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardBody>
      </Card>
    </div>
  );
}
